import { addToCart as addToCartUtil } from '@utilities/cart';
import { addToCartData, removeToCartData, logError } from '@actions/userActions';
import {
    KOT_SESSION_CART_KEY,
    isKotSessionCartKey,
    pickKotSessionForMerge,
} from '@utilities/kotSession';
import type { Dispatch } from '@reduxjs/toolkit';
import type {
    CartItem,
    FoodItem,
    SelectedVariant,
    VariantMap,
    TableCart,
    TabsConfig,
} from '../types';

/**
 * Add a new item (qty = 1) to the active table cart.
 * Skips if the item already exists in the cart.
 */
export function addItemToCart(
    item: FoodItem,
    category: string,
    name: string,
    variantMap: VariantMap,
    tableCart: TableCart,
    dispatch: Dispatch,
    activeTable: string,
    restaurantId?: string
): void {
    const compositeKey = `${category}-${name}`;
    const selVar: SelectedVariant = variantMap[compositeKey] || {
        value: item.variants[0].cost,
        label: `₹${item.variants[0].cost} / ${item.variants[0].size}`,
    };
    const cartKey = `${compositeKey}-${selVar.value}`;

    const existing = tableCart[cartKey] as CartItem | null;
    if (!existing?.selected) {
        addToCartUtil(
            { ...item, category, name, selectedVariants: selVar, variants: null },
            cartKey,
            dispatch,
            activeTable,
            restaurantId,
        );
    }
}

/**
 * Update the quantity of a cart item by a delta (+/−).
 * Removes the item when qty reaches 0.
 */
export function updateItemQty(
    key: string,
    item: CartItem,
    delta: number,
    dispatch: Dispatch,
    activeTable: string,
    restaurantId?: string
): void {
    const next = Math.max(0, Math.min(99, (item.selected || 0) + delta));
    if (next === 0) {
        dispatch(addToCartData({ [key]: null }, activeTable, restaurantId) as any);
    } else {
        dispatch(addToCartData({ [key]: { ...item, selected: next } }, activeTable, restaurantId) as any);
    }
}

/**
 * Remove a single item from the cart (set to null).
 */
export function removeItem(
    key: string,
    dispatch: Dispatch,
    activeTable: string,
    restaurantId?: string
): void {
    dispatch(addToCartData({ [key]: null }, activeTable, restaurantId) as any);
}

/**
 * Merge the source table cart into the destination, combining quantities
 * for matching item keys.
 */
export function mergeTableCarts(
    destCart: TableCart,
    srcCart: TableCart
): TableCart {
    const merged: TableCart = { ...destCart };
    Object.entries(srcCart).forEach(([itemKey, itemDetails]) => {
        if (isKotSessionCartKey(itemKey)) return;
        const detail = itemDetails as CartItem | null;
        const existing = merged[itemKey] as CartItem | null;
        if (existing && detail) {
            merged[itemKey] = {
                ...existing,
                selected: (existing.selected || 0) + (detail.selected || 0),
            };
        } else {
            merged[itemKey] = detail;
        }
    });
    return merged;
}

/**
 * Execute the merge-table workflow: validate dest, merge carts, dispatch actions.
 */
export function executeMergeTable(params: {
    tabs: TabsConfig;
    activeTable: string;
    fullCart: Record<string, TableCart>;
    tableCart: TableCart;
    restaurantId?: string;
    dispatch: Dispatch;
}): void {
    const { tabs, activeTable, fullCart, tableCart, restaurantId, dispatch } = params;

    let destTableNumber: string | null = prompt('Move cart to Table: Enter table name', '');
    if (!destTableNumber) return;

    const allTableNames: string[] = [];
    Object.entries(tabs).forEach(([tab, count]) => {
        for (let i = 1; i <= count; i++) {
            allTableNames.push(`${tab}-${i}`);
        }
    });

    const found = allTableNames.find((str) => {
        if (str.toLowerCase() === destTableNumber!.toLowerCase()) {
            destTableNumber = str;
            return true;
        }
        return false;
    });

    if (found && destTableNumber!.toLowerCase() !== activeTable.toLowerCase()) {
        if (restaurantId && activeTable && destTableNumber) {
            const destCart = (fullCart[destTableNumber] || {}) as TableCart;
            const merged = mergeTableCarts(destCart, tableCart);
            merged[KOT_SESSION_CART_KEY] = pickKotSessionForMerge(
                destCart as Record<string, unknown>,
                tableCart as Record<string, unknown>,
                destTableNumber
            ) as any;
            dispatch(addToCartData({ ...merged }, destTableNumber, restaurantId) as any);
            dispatch(removeToCartData({}, activeTable, restaurantId) as any);
        }
        dispatch(logError({ severity: 'success', message: `Cart moved to ${destTableNumber!.toUpperCase()}` }) as any);
    } else {
        dispatch(logError({ severity: 'error', message: `${(destTableNumber || '').toUpperCase()} doesn't exist!` }) as any);
    }
}
